import { createElement, useState, type ReactNode } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import classNames from 'classnames';
import ClipboardIcon from '@/shared/assets/icons/Clipboard';
import KeyIcon from '@/shared/assets/icons/Key';
import BlockIcon from '@/shared/assets/svg/block.svg';
import { Link } from '@/shared/components/atoms/Link';
import Card from '@/shared/components/flip-ui-kit/Card';
import { type BlockListBlockFragment } from '@/shared/graphql/generated/explorer/graphql';
import useAfterMount from '@/shared/hooks/useAfterMount';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { etherscanUrl } from '@/shared/utils/helpers';
import { formatWithCommas } from '@/shared/utils/numbers';
import { getEnvironmentAddress } from '../../graphql/cache/statechainCache';

type BlinkProps = {
  children: ReactNode;
  className?: string;
};

function Blink({ children, className }: BlinkProps): React.JSX.Element {
  const [state, setState] = useState({
    switching: false,
    preTransitionChild: children,
    child: children,
  });

  useAfterMount(() => {
    setState((previous) => ({ ...previous, switching: true, child: children }));

    const timeout = setTimeout(
      () =>
        setState({
          switching: false,
          preTransitionChild: children,
          child: children,
        }),
      500,
    );

    return () => clearTimeout(timeout);
  }, [children]);

  const switching = state.switching && state.preTransitionChild !== undefined;

  return createElement(
    'div',
    {
      className: classNames(className, 'transition-opacity duration-500', switching && 'opacity-0'),
    },
    switching ? state.preTransitionChild : state.child,
  );
}

interface MetadataProps {
  blockList: BlockListBlockFragment[];
}

export default function MetadataCards({ blockList }: MetadataProps): React.JSX.Element {
  const { data } = useGqlQuery(getEnvironmentAddress, {
    context: { clientName: 'statechainCache' },
  });
  const { stateChainGatewayAddress, keyManagerAddress } = data?.environmentAddress || {};

  return (
    <div className="grid grid-cols-1 gap-5 min-[420px]:grid-cols-2 md:grid-cols-3">
      <Card title="Finalized Block" icon={<BlockIcon />} loading={blockList[0] === undefined}>
        <Blink>{formatWithCommas(blockList[0]?.id)}</Blink>
      </Card>
      <Card
        loading={keyManagerAddress === undefined}
        title="Key Manager Address"
        icon={<KeyIcon className="text-cf-blue-4" />}
      >
        <Link
          target="_blank"
          href={etherscanUrl('address', keyManagerAddress as string)}
          rel="noreferrer"
          underline
        >
          {abbreviate(keyManagerAddress, 4)}
        </Link>
      </Card>
      <Card
        icon={<ClipboardIcon className="text-cf-orange-3" />}
        loading={stateChainGatewayAddress === undefined}
        title="State Chain Gateway Address"
      >
        <Link
          target="_blank"
          href={etherscanUrl('address', stateChainGatewayAddress as string)}
          rel="noreferrer"
          underline
        >
          {abbreviate(stateChainGatewayAddress, 4)}
        </Link>
      </Card>
    </div>
  );
}
